import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { MdOutlinePeopleAlt, MdOutlineSettings, MdOutlineSpeed, MdOutlineMap, MdOutlineShield, MdOutlineMonitor, MdOutlineAssessment } from "react-icons/md";
import { TbLayoutSidebarLeftExpandFilled, TbLayoutSidebarRightExpand } from "react-icons/tb";
import "../styles/Sidebar.css";
import { t } from "../useTranslations";
import settingsService from "../services/SettingsService";
import sessionService from "../services/SessionService";
import userService from "../services/UserService";
import entityService from "../services/EntityService";
import useAccessibleModules from "../hooks/useAccessibleModules";

const getRoleLabel = (role) => {
  const key = `permissions.roles.${role}`;
  const label = t(key);
  return label !== key ? label : role;
};

const MODULE_ICONS = {
  'dashboard': MdOutlineSpeed,
  'customer-risk': MdOutlinePeopleAlt,
  'geographical-risk': MdOutlineMap,
  'bwra': MdOutlineShield,
  'transaction-monitoring': MdOutlineMonitor,
  'periodic-reporting': MdOutlineAssessment,
  'admin': MdOutlineSettings
};

const MODULE_LABELS = {
  'dashboard': 'sidebar.dashboard',
  'customer-risk': 'sidebar.customerRisk',
  'geographical-risk': 'sidebar.geographicalRisk',
  'bwra': 'sidebar.bwra',
  'transaction-monitoring': 'sidebar.transactionMonitoring',
  'periodic-reporting': 'sidebar.periodicReporting',
  'admin': 'sidebar.admin'
};

const Sidebar = ({ isExpanded, toggleSidebar }) => {
  const [showText, setShowText] = useState(false);
  const expandTimerRef = useRef(null);
  const [activeEntity, setActiveEntity] = useState(() => entityService.getActiveEntity());
  const [currentRole, setCurrentRole] = useState(() => {
    const session = sessionService.getSession();
    return session?.username ? userService.getEffectiveRole(session.username) : null;
  });

  const accessibleIds = useAccessibleModules();
  const modules = settingsService.getEnabledModules().filter(m => accessibleIds.includes(m.id));

  useEffect(() => {
    const handleUserChanged = () => {
      const session = sessionService.getSession();
      setCurrentRole(session?.username ? userService.getEffectiveRole(session.username) : null);
    };

    const handleEntityChanged = () => {
      setActiveEntity(entityService.getActiveEntity());
    };

    window.addEventListener('rms_active_entity_changed', handleEntityChanged);
    const unsubUser = userService.subscribe(handleUserChanged);
    const unsubEntity = entityService.subscribe(handleEntityChanged);
    return () => {
      window.removeEventListener('rms_active_entity_changed', handleEntityChanged);
      unsubUser();
      unsubEntity();
      clearTimeout(expandTimerRef.current);
    };
  }, []);

  const handleToggle = () => {
    toggleSidebar();
    if (!isExpanded) {
      // Delay showing text until CSS expansion transition (300ms) completes
      expandTimerRef.current = setTimeout(() => setShowText(true), 310);
    } else {
      clearTimeout(expandTimerRef.current);
      setShowText(false);
    }
  };

  return (
    <div className={`sidebar ${isExpanded ? "expanded" : "collapsed"}`}>
      <div className="toggle-container">
        <button
          className="toggle-button"
          onClick={handleToggle}
          aria-label={isExpanded ? t('sidebar.collapse') : t('sidebar.expand')}
        >
          {isExpanded ? <TbLayoutSidebarRightExpand /> : <TbLayoutSidebarLeftExpandFilled />}
        </button>
      </div>
      <ul>
        {modules.map(mod => {
          const IconComponent = MODULE_ICONS[mod.id];
          const labelKey = MODULE_LABELS[mod.id];
          if (!IconComponent || !labelKey) return null;
          return (
            <li key={mod.id}>
              <NavLink
                to={mod.route}
                className={({ isActive }) => (isActive ? "active" : "")}
                aria-label={t(labelKey)}
              >
                <IconComponent className="icon" />
                {showText && <span className="sidebar-text">{t(labelKey)}</span>}
              </NavLink>
            </li>
          );
        })}
      </ul>
      {activeEntity && (
        <div
          className={`sidebar-entity-indicator${showText ? "" : " sidebar-entity-indicator--compact"}`}
          title={activeEntity.name}
        >
          <span className="sidebar-entity-dot"></span>
          {showText && <span className="sidebar-entity-name">{activeEntity.name}</span>}
        </div>
      )}
      {currentRole && (
        <div className={`sidebar-role-badge${showText ? "" : " sidebar-role-badge--compact"}`}>
          {showText ? (
            <>
              <span>{t('sidebar.roleLabel')}:</span>
              <span className="sidebar-role-label">{getRoleLabel(currentRole)}</span>
            </>
          ) : (
            <span
              className="sidebar-role-label sidebar-role-label--compact"
              title={getRoleLabel(currentRole)}
              aria-label={getRoleLabel(currentRole)}
            >
              {getRoleLabel(currentRole).substring(0, 2).toUpperCase()}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
